import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { 
  ArrowLeft, Plus, Search, Trash2, Edit2, Loader2, 
  Package, RefreshCw, ExternalLink, Home, Settings, LogOut
} from "lucide-react";
import { toast } from "sonner";

const Materials = ({ user, API }) => {
  const navigate = useNavigate();
  
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scraping, setScraping] = useState(false);
  
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isScrapeDialogOpen, setIsScrapeDialogOpen] = useState(false);
  
  // New material form
  const [newMaterial, setNewMaterial] = useState({
    name: "",
    price: "",
    unit: "each",
    category: "General"
  });
  
  // Scrape form
  const [scrapeQuery, setScrapeQuery] = useState("");
  const [scrapeCategory, setScrapeCategory] = useState("General");
  
  useEffect(() => {
    fetchMaterials();
    fetchCategories();
  }, []);
  
  const fetchMaterials = async () => {
    try {
      const response = await fetch(`${API}/materials`, {
        credentials: "include"
      });
      if (!response.ok) throw new Error("Failed to fetch");
      const data = await response.json();
      setMaterials(data);
    } catch (error) {
      toast.error("Failed to load materials");
    } finally {
      setLoading(false);
    }
  };
  
  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API}/scrape/categories`, {
        credentials: "include"
      });
      if (!response.ok) throw new Error("Failed to fetch");
      const data = await response.json();
      setCategories(data.categories);
    } catch (error) {
      setCategories(["General", "Pipes & Fittings", "Tools"]);
    }
  };
  
  const handleAddMaterial = async () => {
    if (!newMaterial.name || !newMaterial.price) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    try {
      const response = await fetch(`${API}/materials`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...newMaterial,
          price: parseFloat(newMaterial.price)
        })
      });
      
      if (!response.ok) throw new Error("Failed to add");
      
      const material = await response.json();
      setMaterials([material, ...materials]);
      setNewMaterial({ name: "", price: "", unit: "each", category: "General" });
      setIsAddDialogOpen(false);
      toast.success("Material added successfully");
    } catch (error) {
      toast.error("Failed to add material");
    }
  };
  
  const handleDeleteMaterial = async (materialId) => {
    if (!confirm("Are you sure you want to delete this material?")) return;
    
    try {
      const response = await fetch(`${API}/materials/${materialId}`, {
        method: "DELETE",
        credentials: "include"
      });
      
      if (!response.ok) throw new Error("Failed to delete");
      
      setMaterials(materials.filter(m => m.material_id !== materialId));
      toast.success("Material deleted");
    } catch (error) {
      toast.error("Failed to delete material");
    }
  };
  
  const handleScrape = async () => {
    if (!scrapeQuery.trim()) {
      toast.error("Please enter a search query");
      return;
    }
    
    setScraping(true);
    try {
      const response = await fetch(`${API}/scrape/screwfix`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          search_query: scrapeQuery,
          category: scrapeCategory
        })
      });
      
      if (!response.ok) throw new Error("Failed to scrape");
      
      const data = await response.json();
      toast.success(data.message || "Scraping complete");
      setIsScrapeDialogOpen(false);
      setScrapeQuery("");
      fetchMaterials();
    } catch (error) {
      toast.error("Failed to scrape Screwfix");
    } finally {
      setScraping(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include"
      });
      navigate("/", { replace: true });
    } catch (error) {
      navigate("/", { replace: true });
    }
  };
  
  const filteredMaterials = materials.filter(m => {
    const matchesSearch = m.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "all" || m.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };
  
  const uniqueCategories = [...new Set(materials.map(m => m.category))];
  
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/dashboard")}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="text-xl font-bold">Materials Database</h1>
            </div>

            <nav className="flex items-center gap-2">
              <Link to="/dashboard">
                <Button variant="ghost" size="icon">
                  <Home className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/settings">
                <Button variant="ghost" size="icon">
                  <Settings className="w-5 h-5" />
                </Button>
              </Link>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        {/* Actions */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              data-testid="search-input"
              placeholder="Search materials..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-input/50 border-white/10"
            />
          </div>
          
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-full sm:w-48 bg-input/50 border-white/10" data-testid="category-filter">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {uniqueCategories.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <div className="flex gap-2">
            {/* Scrape Dialog */}
            <Dialog open={isScrapeDialogOpen} onOpenChange={setIsScrapeDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="secondary" data-testid="scrape-btn">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Scrape Screwfix
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border">
                <DialogHeader>
                  <DialogTitle>Scrape Screwfix Prices</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Search Query</Label>
                    <Input
                      data-testid="scrape-query-input"
                      placeholder="e.g., copper pipe 15mm"
                      value={scrapeQuery}
                      onChange={(e) => setScrapeQuery(e.target.value)}
                      className="bg-input/50 border-white/10"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select value={scrapeCategory} onValueChange={setScrapeCategory}>
                      <SelectTrigger className="bg-input/50 border-white/10" data-testid="scrape-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={handleScrape}
                    disabled={scraping}
                    className="w-full bg-primary text-primary-foreground"
                    data-testid="execute-scrape-btn"
                  >
                    {scraping ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Scraping...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Scrape Prices
                      </>
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            
            {/* Add Dialog */}
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary text-primary-foreground" data-testid="add-material-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Material
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border">
                <DialogHeader>
                  <DialogTitle>Add New Material</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Name *</Label>
                    <Input
                      data-testid="new-material-name"
                      placeholder="e.g., 15mm Copper Pipe"
                      value={newMaterial.name}
                      onChange={(e) => setNewMaterial({...newMaterial, name: e.target.value})}
                      className="bg-input/50 border-white/10"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Price (£) *</Label>
                      <Input
                        data-testid="new-material-price"
                        type="number"
                        placeholder="0.00"
                        value={newMaterial.price}
                        onChange={(e) => setNewMaterial({...newMaterial, price: e.target.value})}
                        className="bg-input/50 border-white/10"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Unit</Label>
                      <Select 
                        value={newMaterial.unit} 
                        onValueChange={(v) => setNewMaterial({...newMaterial, unit: v})}
                      >
                        <SelectTrigger className="bg-input/50 border-white/10" data-testid="new-material-unit">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="each">Each</SelectItem>
                          <SelectItem value="metre">Metre</SelectItem>
                          <SelectItem value="pack">Pack</SelectItem>
                          <SelectItem value="kg">Kg</SelectItem>
                          <SelectItem value="litre">Litre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select 
                      value={newMaterial.category} 
                      onValueChange={(v) => setNewMaterial({...newMaterial, category: v})}
                    >
                      <SelectTrigger className="bg-input/50 border-white/10" data-testid="new-material-category">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={handleAddMaterial}
                    className="w-full bg-primary text-primary-foreground"
                    data-testid="save-material-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Material
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Materials List */}
        <Card className="bg-card border-white/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-primary" />
              Materials ({filteredMaterials.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-16 bg-secondary/50 animate-pulse rounded-lg" />
                ))}
              </div>
            ) : filteredMaterials.length > 0 ? (
              <div className="space-y-3">
                {filteredMaterials.map((material) => (
                  <div
                    key={material.material_id}
                    data-testid={`material-${material.material_id}`}
                    className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg hover:bg-secondary/50 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-semibold">{material.name}</h3>
                        <Badge variant="secondary" className="text-xs">
                          {material.category}
                        </Badge>
                        {material.source !== "manual" && (
                          <Badge className="bg-blue-500/20 text-blue-400 text-xs">
                            {material.source}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        per {material.unit}
                        {material.source_url && (
                          <a 
                            href={material.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-2 inline-flex items-center text-primary hover:underline"
                          >
                            <ExternalLink className="w-3 h-3 mr-1" />
                            View Source
                          </a>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="mono text-xl font-bold text-primary">
                        {formatCurrency(material.price)}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteMaterial(material.material_id)}
                        className="text-muted-foreground hover:text-destructive"
                        data-testid={`delete-material-${material.material_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">No materials found</p>
                <Button
                  onClick={() => setIsAddDialogOpen(true)}
                  className="bg-primary text-primary-foreground"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Material
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Materials;
